const React = require('react')
const PropTypes = require('prop-types')
const Page = require('app/page')
const classNames = require('classnames')
const withStyles = require('withStyles').default
const styles = require('./profile.less')
const MappedNav = require('./mapped_nav')
const ProfileView = require('module/profile_view/profile_view')
const FooterLogo = require('./footer_logo')
const isUser = require('module/user/helpers/is_user')
const getLayoutColor = require('module/user/helpers/get_layout_color')
const isLoggedIn = require('module/viewer/helpers/is_logged_in')
const ProfilePrompt = require('module/profile_prompt/profile_prompt')
const ViewerPanel = require('module/viewer_panel/viewer_panel')
const hasFeature = require('module/user/helpers/has_feature')
const { AnonymousComponent } = require('component/viewer/anonymous')
const BannerComponent = require('module/banner/banner_component')
const funnels = require('lib/funnels')
const getLuminance = require('lib/get_luminance')
const radio = require('lib/radio')
const log = require('log').create('profile') // eslint-disable-line


class Profile extends React.Component {

    componentDidMount() {
        this.sendMetrics()
        this.sendViewAjax()
        this.provideUserModel()
        this.initializeUserRouter()
    }

    componentWillUnmount() {
        this.channel.stopReplying('user')
    }

    getRenderProps() {
        const { viewer, page, globals, router } = this.context

        let mapped = !! globals.MAPPED
        if (__DEBUG__) {
            // allow mapped override in dev with ?mapped=true
            if (router.location.query.mapped === 'true') mapped = true
        }

        const { user } = page
        const is_owner = isUser(viewer, user)
        const is_logged_in = isLoggedIn(viewer)
        const owner = is_owner ? viewer : user

        return {
            page,
            viewer,
            is_owner,
            is_logged_in,
            owner,
            mapped,
        }
    }

    shouldRenderNav(props) {
        const is_less_branding = funnels.get('20230206_less_branding').is('experiment')

        if (hasFeature(props.owner, 'upgrade') && ! props.is_logged_in) return false
        if (props.mapped) return false
        if (props.is_logged_in) return true
        if (props.is_owner) return true
        if (is_less_branding) return false
        return true
    }

    shouldRenderLedge(props) {
        const is_less_branding = funnels.get('20230206_less_branding').is('experiment')

        if (props.mapped) return false
        if (props.is_logged_in) return false
        if (is_less_branding) return false
        if (hasFeature(props.owner, 'upgrade')) return false
        return true
    }

    sendMetrics() {
        const metrics = radio.metrics_profile
        const { user } = this.context.page

        metrics.trigger('profile:viewed', {
            user_name: user.user_name,
        })
    }

    sendViewAjax() {
        const { viewer } = this.context
        const { user_name } = this.context.page.user
        const is_owner = isUser(viewer, user_name)

        // We should not record a page view if the viewer is the page owner.
        if (is_owner) return

        // Viewer is not the page owner... do everything we need to record a
        // page view.
        const $ = require('lib/jquery')
        const { NODE_API, DOMAIN_NAME } = this.context.globals

        let referrer = document.referrer || ''
        if (referrer.indexOf(DOMAIN_NAME) !== -1) referrer = ''

        const data = {
            user_name,
            events: [],
        }

        // yeah, this format is weird, due to a legacy node-api thing
        if (referrer) {
            data.events.push(`referrer.${referrer}`)
        }

        // silent ajax call, 'cause we don't care if it succeeds
        $.ajax({
            type: 'POST',
            url: `${NODE_API}/user/performance/record/views`,
            data: JSON.stringify(data),
            dataType: 'json',
            headers: {
                'Content-Type': 'application/json',
            },
        })
    }

    provideUserModel() {
        // some BB/MM components request a user model from
        // radio.profile.request('user')
        const ProfileUser = require('module/user/profile_user')
        const { owner } = this.getRenderProps()
        this.user_model = new ProfileUser(owner)
        this.channel = radio.profile
        this.channel.reply('user', () => this.user_model)
    }

    initializeUserRouter() {
        require.ensure([], () => {
            const ProfileRouter = require('./profile_router')
            this.profile_router = new ProfileRouter({
                user: this.user_model,
            })
        }, 'profile-router')
    }

    render() {
        const { DOMAIN_NAME_URL } = this.context.globals

        const props = this.getRenderProps()
        const {
            page,
            viewer,
            is_owner,
            is_logged_in,
            owner,
            mapped,
        } = props

        // don't show nav on mapped pages
        const render_nav = this.shouldRenderNav(props)
        const render_ledge = this.shouldRenderLedge(props)

        // show the about.me footer logo if the funnel is experiment
        // but never show it for pro users
        const is_less_branding = funnels.get('20230206_less_branding').is('experiment')
        const render_footer_logo = is_less_branding && ! hasFeature(owner, 'upgrade')

        // show cookie notice on mapped pages only if they want it
        // note: page.show_cookie_notice is used by profile_tester.js to kill the notice
        // when testing the profile page
        const show_cookie_notice = (
            page.show_cookie_notice === undefined
                ? (! mapped || page.mapped_cookie_notice)
                : page.show_cookie_notice
        )

        const layout_color = getLayoutColor(owner)
        const luminance = getLuminance(layout_color)
        const template_id = owner.layout.template_id

        const classnames = classNames([
            `color-${luminance}`,
            styles.scope,
            this.props.className,
        ])

        // note: important that ProfileView is the first node inside main, in
        // order for flex-fill rules to work

        return (
            <Page
                render_nav={render_nav}
                render_footer={false}
                className={classnames}
                show_cookie_notice={show_cookie_notice}
                >

                <div className="profile-column">
                    <ProfileView
                        user={owner}
                        mapped={mapped}
                        nav={render_nav}
                        ledge={render_ledge}
                        />

                    {render_footer_logo && <FooterLogo template={owner.layout.template_id} />}
                </div>

                {render_ledge && <AnonymousComponent>
                    <ProfilePrompt
                        page={page}
                        viewer={viewer}
                        is_owner={is_owner}
                        mapped={mapped}
                        is_logged_in={is_logged_in}
                        />
                </AnonymousComponent>}

                <ViewerPanel is_owner={is_owner} mapped={mapped} />

                {owner.unpublished && (
                    <BannerComponent text="This user is unpublished." duration={0} />
                )}

                <MappedNav
                    mapped={mapped}
                    host={DOMAIN_NAME_URL}
                    owner={owner}
                    luminance={luminance}
                    template_id={template_id}
                    />

            </Page>
        )
    }

}

Profile.propTypes = {
    className: PropTypes.string,
}

Profile.contextTypes = {
    globals: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    page: PropTypes.shape({
        user: PropTypes.object.isRequired,
    }).isRequired,
    router: PropTypes.shape({
        location: PropTypes.object,
    }).isRequired,
}

module.exports = withStyles(styles)(Profile)
module.exports.ProfileComponent = Profile
